import { Link } from 'wouter';
import { api, type Card, type SentYesterday } from '../lib/api';
import { useResource } from '../lib/use-resource';
import { ageOf, whenLocal } from '../lib/time';
import { cardLook, decidedBy, who } from '../lib/cards';
import './done.css';

/**
 * What happened today, and WHO decided it.
 *
 * Three things look alike in the database and are not alike at all: the
 * operator decided, a veto window expired with nobody objecting, and the one
 * automatic action fired. A list that showed them identically would hide the
 * only question worth asking of this screen — did anything happen today that
 * nobody chose?
 */
export function DoneToday({ me = null }: { me?: string | null }) {
  const r = useResource<{ cards: Card[] }>(() => api.cards('done_today', 'newest'), { pollMs: 60_000 });
  // Yesterday's automatic sends, from the SAME query the 09:00 digest runs, so
  // this screen and the Slack post cannot disagree about what the system did
  // on its own.
  const y = useResource<SentYesterday>(() => api.sentYesterday(), { pollMs: 300_000 });
  const cards = r.data?.cards ?? [];
  const automatic = cards.filter((c) => c.decided_by?.startsWith('auto')).length;

  return (
    <main className="board">
      <div>
        <div className="mono eyebrow">Done today</div>
        <h1>What happened, and who decided it</h1>
        <p className="lede">
          {r.loading && !r.data
            ? 'Reading today…'
            : cards.length === 0
              ? 'Nothing has been decided today.'
              : `${cards.length} card${cards.length === 1 ? '' : 's'}, ${automatic} of them without a person.`}
        </p>
      </div>

      {r.error && <p className="list-empty list-error">{r.error.message}</p>}

      {/* What went out yesterday without anybody clicking. Zero is printed, not
          hidden: a section that vanishes when it is zero cannot be told apart
          from a section that failed to load, and "the system sent nothing on
          its own yesterday" is a fact worth reading. */}
      {y.data && (
        <section className="auto-sends">
          <div className="auto-head">
            <span className="mono eyebrow">Sent without you · {y.data.day}</span>
            <span className="auto-total">{y.data.note}</span>
          </div>
          {y.data.by_class.length > 0 && (
            <div className="auto-rows">
              {y.data.by_class.map((a) => (
                <span key={`${a.class}-${a.template_id}`} className="auto-chip mono">
                  {a.count} × {a.class.replace(/_/g, ' ')}
                  {a.template_id ? ` · T${a.template_id}` : ''}
                </span>
              ))}
            </div>
          )}
        </section>
      )}

      <div className="done-rows">
        {cards.map((card) => {
          const look = cardLook(card);
          return (
            <Link key={card.id} href={`/cards/${card.id}`} className={`done-row status-${card.status}`}>
              <span className={`badge mono badge-${look.tone}`}>{look.label}</span>
              <div className="done-main">
                <div className="done-title">{card.summary || card.title}</div>
                <div className="mono done-sub">{who(card)}</div>
                {card.error_text && <div className="mono done-error">{card.error_text}</div>}
              </div>
              <div className="done-decision">
                <span className={`done-status status-word-${card.status}`}>{card.status}</span>
                <span className="mono done-by">by {decidedBy(card.decided_by, me)}</span>
              </div>
              <div className="mono done-when">
                {whenLocal(card.executed_at ?? card.decided_at)}
                <span className="done-ago"> · {ageOf(card.executed_at ?? card.decided_at)} ago</span>
              </div>
            </Link>
          );
        })}
      </div>
    </main>
  );
}
